import React, { useState, useRef, MutableRefObject, RefAttributes, Ref, useEffect, useCallback, createRef } from 'react';
import theme from './Tooltip.module.scss';
import { useDebounce } from '@/utils/hooks';
import { classnames } from '@/utils/html';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { usePopper } from 'react-popper';
import { TooltipProps, TooltipChildren, TooltipQuestionMark } from '.';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';

function combineRefs<T = any>(node: T, ...refs: (Ref<T> | null | undefined)[]): void {
    refs.forEach((ref) => {
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref !== null) {
            (ref as MutableRefObject<any>).current = node;
        }
    });
    // Call the original ref, if any
    // if (typeof originalRef === 'function') {
    //     originalRef(originalRef);
    // } else if (originalRef) {
    //     (originalRef as MutableRefObject<any>).current = node;
    // }
}

const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
    (
        {
            children,
            content,
            placement,
            color = 'dark',
            dynamicWidth = false,
            forceOpen = false,
            showOnHover = true,
            interactive = true,
            noPadding = false,
            delay = 0,
            onShow,
            onHide,
            title,
            className,
            includeQuestionMark,
            minWidthPx: minWidth,
        },
        tooltipRef
    ) => {
        const child = React.Children.only<TooltipChildren>(
            includeQuestionMark ? (
                <span className="flex">
                    {children} <TooltipQuestionMark />{' '}
                </span>
            ) : (
                (children as TooltipChildren)
            )
        );
        const hideTimeoutRef = useRef<number | null>(null);

        const [visible, setVisible] = useState(false);
        let shouldRender = useDebounce(visible || forceOpen, 250);

        const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
        const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
        const { styles, attributes, state } = usePopper(referenceElement, popperElement, {
            placement: placement,
            modifiers: [
                {
                    name: 'computeStyles',
                    options: {
                        gpuAcceleration: false,
                    },
                },
                {
                    name: 'offset',
                    options: {
                        offset: [0, 8],
                    },
                },
            ],
        });

        const hideTooltip = useCallback(() => {
            if (hideTimeoutRef.current) {
                window.clearTimeout(hideTimeoutRef.current);
            }

            if (!showOnHover) {
                return;
            }

            hideTimeoutRef.current = window.setTimeout(() => {
                triggerHandler(onHide);
                setVisible(false);
            }, 150);
        }, [showOnHover, onHide]);

        const showTooltip = useCallback(() => {
            if (hideTimeoutRef.current) {
                window.clearTimeout(hideTimeoutRef.current);
            }

            if (!showOnHover) {
                return;
            }

            hideTimeoutRef.current = window.setTimeout(() => {
                triggerHandler(onShow);
                setVisible(true);
            }, delay);
        }, [showOnHover, onShow, delay]);

        useEffect(() => {
            const el = referenceElement;

            if (el) {
                el.addEventListener('mouseenter', showTooltip);
                el.addEventListener('mouseleave', hideTooltip);
            }

            return function cleanup() {
                if (el) {
                    el.removeEventListener('mouseenter', showTooltip);
                    el.removeEventListener('mouseleave', hideTooltip);
                }
            };
        }, [referenceElement, showTooltip, hideTooltip]);

        if (forceOpen) {
            shouldRender = true;
        }

        if (!content) {
            return <>{children}</>;
        }

        return (
            <>
                {React.cloneElement(child, {
                    // ref: ref,
                    ref: (node) => {
                        // const childWithRef = child as typeof child & RefAttributes<any>;
                        // const { ref: outerRef } = childWithRef;
                        
                        // combineRefs(node, setReferenceElement, outerRef);
                        setReferenceElement(node);
                    },
                    className: (child.props.className || '') + ' ' + theme.triggerElement,
                    // onMouseEnter: (e: SyntheticEvent) => showTooltip(),
                    // onMouseLeave: (e: SyntheticEvent) => hideTooltip(),
                })}
                {shouldRender && (
                    <div
                        {...attributes.popper}
                        className={classnames(theme.tooltip, state ? theme[state.placement] : null, className, {
                            [theme.interactive]: interactive,
                        })}
                        ref={(node) => combineRefs(node, setPopperElement, tooltipRef)}
                        style={styles.popper as any}
                        onClick={(e) => {
                            // Allow link clicks to work, only stop propagation for other clicks
                            if ((e.target as HTMLElement).tagName !== 'A') {
                                e.stopPropagation();
                            }
                        }}
                        onMouseEnter={(e) => {
                            if (visible) showTooltip();
                        }}
                        onMouseLeave={(e) => hideTooltip()}>
                        <div
                            style={{ minWidth: minWidth }}
                            className={classnames(theme.content, theme[color], {
                                [theme.dynamicWidth]: dynamicWidth,
                                [theme.removed]: !visible && !forceOpen,
                                [theme.noPadding]: noPadding,
                            })}>
                            <FontAwesomeIcon icon={faCaretUp} className={theme.triangle} />
                            {title && <div className={theme.title}>{title}</div>}
                            {content}
                        </div>
                    </div>
                )}
            </>
        );
    }
);

export default Tooltip;
